// Topbar breadcrumbs: region name on regional routes only (not on the global ops, admin and about pages), then route crumbs from staticData.crumb, or crumbs a screen sets (Workspace: document and clause).
import { useMatches } from "@tanstack/react-router";
import { createContext, Fragment, use, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface CrumbOverrideContextValue {
  override: readonly string[] | null;
  setOverride: (crumbs: readonly string[] | null) => void;
}

const CrumbOverrideContext = createContext<CrumbOverrideContextValue | null>(null);

export function CrumbsProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<readonly string[] | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return <CrumbOverrideContext value={value}>{children}</CrumbOverrideContext>;
}

export function useCrumbOverride(crumbs: readonly string[] | null): void {
  const setOverride = use(CrumbOverrideContext)?.setOverride;
  const key = crumbs === null ? null : JSON.stringify(crumbs);
  useEffect(() => {
    if (setOverride === undefined || key === null) {
      return undefined;
    }
    setOverride(JSON.parse(key) as string[]);
    return () => {
      setOverride(null);
    };
  }, [setOverride, key]);
}

export function Breadcrumbs({ regionName }: { regionName: string | null }) {
  const matches = useMatches();
  const context = use(CrumbOverrideContext);
  const failed = matches.some((match) => match.status === "notFound" || match.status === "error");
  const routeCrumbs = failed
    ? []
    : matches.flatMap((match) => (match.staticData.crumb === undefined ? [] : [match.staticData.crumb]));
  const regional = matches.some((match) => match.routeId === "/_app/$region");
  const regionCrumbs = regional && regionName !== null ? [regionName] : [];
  const crumbs = [...regionCrumbs, ...(context?.override ?? routeCrumbs)];
  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList>
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <Fragment key={`${String(index)}-${crumb}`}>
              <BreadcrumbItem>
                {last ? <BreadcrumbPage>{crumb}</BreadcrumbPage> : <span className="truncate">{crumb}</span>}
              </BreadcrumbItem>
              {last ? null : <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
